import type { NormalizedWebformItem } from './asWebformItem'

const SELECT_TYPENAMES = new Set([
  'WebformElementSelect',
  'WebformElementWebformEntitySelect',
  'WebformElementWebformSelectOther',
  'WebformElementWebformTermSelect',
])

const CHECKBOX_GROUP_TYPENAMES = new Set([
  'WebformElementCheckboxes',
  'WebformElementWebformCheckboxesOther',
])

/**
 * Initial FormKit value for a webform element.
 *
 * Native HTML (and Drupal) selects without an empty option show *and submit*
 * the first option. FormKit with value `''` still *displays* that first option
 * but keeps an empty model, so required selects fail even when they look
 * filled. Match Drupal: use the empty option's value when present, otherwise
 * the first option.
 *
 * Checkbox groups must start as `[]`. FormKit coerces a non-array model to an
 * empty array when options are present; starting as `''` fights that and can
 * leave the group empty for Drupal even when options look checked.
 */
export function getWebformItemInitialValue(
  item: NormalizedWebformItem,
): string | string[] {
  const { defaultValue, element } = item
  const typename = element?.__typename ?? ''

  if (CHECKBOX_GROUP_TYPENAMES.has(typename)) {
    if (!defaultValue) {
      return []
    }
    try {
      const parsed: unknown = JSON.parse(defaultValue)
      if (Array.isArray(parsed)) {
        return parsed.map(String)
      }
    } catch {
      // Drupal may send a single option value as a plain string.
    }
    return [defaultValue]
  }

  if (defaultValue) {
    return defaultValue
  }

  if (!element || !SELECT_TYPENAMES.has(typename)) {
    return ''
  }

  const emptyOption = 'emptyOption' in element ? element.emptyOption : null
  if (emptyOption) {
    return emptyOption.value ?? ''
  }

  const firstOption =
    'options' in element
      ? element.options?.find((option) => option != null)
      : null
  return firstOption?.value ?? ''
}

/**
 * Given normalized webform elements, build the FormKit data definition.
 */
export function populateFormData(
  items: NormalizedWebformItem[] | null = [],
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  data: Record<string, any>,
) {
  items?.forEach((item) => {
    if (item.element?.__typename === 'WebformElementWebformCustomComposite') {
      return
    }
    const { name } = item
    if (name) {
      data[name] = getWebformItemInitialValue(item)
    }
    if (item.element && 'items' in item.element) {
      populateFormData(item.element.items, data)
    }
  })

  return data
}
